import React, { useMemo, useCallback, useReducer, useEffect, useContext } from 'react';
import { Form } from '../ui-kit';
import { xor, without, intersection, isEmpty, union, sortBy, isEqual } from 'lodash';
import { usePrevious } from '../../utils/custom-hooks';

import Loader from '../general/Loader';

const Context = React.createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case 'toggleGroup': {
      return toggleGroup(state, action.payload);
    }
    case 'toggleAvailabilityRequirement': {
      return toggleAvailabilityRequirement(state, action.payload);
    }
    default: {
      throw new Error('unknown action');
    }
  }
};

const isGroupSelected = (group, selectedAvailabilityRequirements) => {
  return !isEmpty(
    intersection(
      selectedAvailabilityRequirements,
      group.availability_requirements.map(ar => ar.id)
    )
  );
};

const toggleGroup = (state, group) => {
  if (isGroupSelected(group, state.selectedAvailabilityRequirements)) {
    // De-select all group's availability requirements
    return {
      ...state,
      selectedAvailabilityRequirements: without(
        state.selectedAvailabilityRequirements,
        ...group.availability_requirements.map(ar => ar.id)
      )
    };
  }

  // Select all group's availability requirements
  return {
    ...state,
    selectedAvailabilityRequirements: union(
      state.selectedAvailabilityRequirements,
      group.availability_requirements.map(ar => ar.id)
    )
  };
};

const toggleAvailabilityRequirement = (state, ar) => {
  return {
    ...state,
    selectedAvailabilityRequirements: xor(state.selectedAvailabilityRequirements, [ar.id])
  };
};

const GroupTreeElement = ({ group, childNodes, level }) => {
  const { state, dispatch } = useContext(Context);

  const handleGroupChange = useCallback(
    group => {
      dispatch({ type: 'toggleGroup', payload: group });
    },
    [dispatch]
  );

  const handleAvailabilityRequirementChange = useCallback(
    ar => {
      dispatch({ type: 'toggleAvailabilityRequirement', payload: ar });
    },
    [dispatch]
  );

  if (!group) return null;
  if (group.type !== 'station' || isEmpty(group.availability_requirements))
    return <GroupTree tree={childNodes} parent={group} level={level + 1} />;

  return (
    <div style={{ marginLeft: '20px' }}>
      <Form.Check
        id={`select_all_group_${group.id}`}
        checked={isGroupSelected(group, state.selectedAvailabilityRequirements)}
        onChange={() => handleGroupChange(group)}
        label={group.name}
      />
      {group.availability_requirements.map(ar => (
        <Form.Check
          id={`select_ar_${ar.id}`}
          key={ar.id}
          style={{ marginLeft: '20px' }}
          checked={state.selectedAvailabilityRequirements.includes(ar.id)}
          onChange={() => handleAvailabilityRequirementChange(ar)}
          label={ar.name}
        />
      ))}
      <GroupTree tree={childNodes} parent={group} level={level + 1} />
    </div>
  );
};

const RegionName = ({ group, level }) => {
  // Do not render name if group is either a station or a team
  // Also do not render on level 0 because we don't have details on
  // the tree root
  if (level == 0 || ['station', 'team'].includes(group.type)) return null;
  return <label>{group.name}</label>;
};

const GroupTree = ({ tree, parent, level = 0 }) => {
  const treeEntries = Object.entries(tree);

  if (isEmpty(treeEntries)) return null;

  const sortedGroups = sortBy(treeEntries, ([_groupId, subTree]) => {
    return subTree.data.name;
  });

  return (
    <div style={{ marginLeft: `${level * 10}px` }}>
      <RegionName group={parent} level={level} />
      {sortedGroups.map(([_groupId, subTree]) => {
        return (
          <GroupTreeElement key={subTree.data.id} group={subTree.data} childNodes={subTree.children} level={level} />
        );
      })}
    </div>
  );
};

// Walks the tree until parent's subtree is found
const findParentScopedTree = (tree, parentId) => {
  let found = null;
  for (const [groupId, subTree] of Object.entries(tree)) {
    if (groupId == parentId.toString()) {
      found = { [groupId]: subTree };
      break;
    }
    if (Object.entries(subTree.children).length > 0) {
      found = findParentScopedTree(subTree.children, parentId);
      if (found) break;
    }
  }

  return found;
};

const AvailabilityRequirementsSelector = ({
  groupsTree,
  parentGroupId,
  selectedAvailabilityRequirements,
  onChange
}) => {
  // useMemo memoizes the expensive tree calculation for every re-render
  const scopedTree = useMemo(() => {
    if (parentGroupId) return findParentScopedTree(groupsTree, parentGroupId);
    return groupsTree;
  }, [groupsTree, parentGroupId]);

  // Using a reducer to ensure states changes always happen top down
  const [state, dispatch] = useReducer(reducer, {
    selectedAvailabilityRequirements
  });

  const previousState = usePrevious(state);

  // Notify onChange to the parent component on every state change
  useEffect(() => {
    if (isEqual(previousState, state) || !onChange) return;

    onChange(state);
  }, [state, onChange, previousState]);

  if (isEmpty(groupsTree)) return <Loader />;

  return (
    <Context.Provider value={{ state, dispatch }}>
      <Form.Group>
        <label>{I18n.t('activerecord.attributes.user.visible_availability_requirement_ids')}</label>
        <GroupTree tree={scopedTree} parent={scopedTree[parentGroupId].data} />
      </Form.Group>
    </Context.Provider>
  );
};

export default AvailabilityRequirementsSelector;
